import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  deleteObject,
  listAll,
  getMetadata,
  updateMetadata,
  getStorage
} from "firebase/storage";
import { initializeApp, getApps, getApp } from 'firebase/app';

// Firebase 설정
const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID
};

// Firebase 앱 초기화 (중복 방지)
const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
const storage = getStorage(app);

class StorageService {
  constructor() {
    this.storage = storage;
  }

  // 파일 업로드 (개선된 에러 처리)
  async uploadFile(path, file, onProgress = null) {
    try {
      const storageRef = ref(this.storage, `${path}/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            if (onProgress && typeof onProgress === 'function') {
              onProgress(progress);
            }
          },
          (error) => {
            const errorMessage = this.getStorageErrorMessage(error.code);
            reject({
              success: false,
              error: errorMessage,
              code: error.code
            });
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                success: true,
                downloadURL,
                fileName: file.name,
                fileSize: file.size,
                contentType: file.type,
                error: null
              });
            } catch (error) {
              reject({
                success: false,
                error: "다운로드 URL을 가져오는데 실패했습니다.",
                code: error.code
              });
            }
          }
        );
      });
    } catch (error) {
      return {
        success: false,
        error: error.message,
        code: error.code
      };
    }
  }

  // Blob URL 업로드
  async uploadBlobUrl(path, fileName, blobUrl, onProgress = null) {
    try {
      let blob;
      
      // blobUrl이 이미 Blob 객체인 경우
      if (blobUrl instanceof Blob) {
        blob = blobUrl;
      } else {
        // blobUrl이 URL인 경우
        const response = await fetch(blobUrl, {
          mode: 'cors',
          credentials: 'same-origin'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        blob = await response.blob();
      }

      const file = new File([blob], fileName, { type: blob.type });
      return await this.uploadFile(path, file, onProgress);
    } catch (error) {
      return {
        success: false,
        error: error.message,
        code: error.code
      };
    }
  }

  // 파일 삭제
  async deleteFile(path, fileName) {
    try {
      const fileRef = ref(this.storage, `${path}/${fileName}`);
      await deleteObject(fileRef);
      
      return {
        success: true,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        error: this.getStorageErrorMessage(error.code)
      };
    }
  }

  // URL에서 파일 삭제
  async deleteFileFromUrl(path, fileUrl) {
    try {
      const fileName = this.extractFileNameFromUrl(fileUrl);
      if (!fileName) {
        return {
          success: false,
          error: "URL에서 파일명을 추출할 수 없습니다."
        };
      }
      
      return await this.deleteFile(path, fileName);
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 파일 메타데이터 가져오기
  async getFileMetadata(path, fileName) {
    try {
      const fileRef = ref(this.storage, `${path}/${fileName}`);
      const metadata = await getMetadata(fileRef);
      
      return {
        success: true,
        metadata,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        metadata: null,
        error: this.getStorageErrorMessage(error.code)
      };
    }
  }

  // 파일 메타데이터 업데이트
  async updateFileMetadata(path, fileName, metadata) {
    try {
      const fileRef = ref(this.storage, `${path}/${fileName}`);
      await updateMetadata(fileRef, metadata);
      
      return {
        success: true,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        error: this.getStorageErrorMessage(error.code)
      };
    }
  }

  // 폴더 내 모든 파일 목록 가져오기
  async listFiles(path) {
    try {
      const folderRef = ref(this.storage, path);
      const result = await listAll(folderRef);
      
      const files = [];
      for (const item of result.items) {
        try {
          const metadata = await getMetadata(item);
          const downloadURL = await getDownloadURL(item);
          files.push({
            name: item.name,
            fullPath: item.fullPath,
            size: metadata.size,
            contentType: metadata.contentType,
            timeCreated: metadata.timeCreated,
            updated: metadata.updated,
            downloadURL
          });
        } catch (error) {
          console.warn(`파일 메타데이터를 가져오는데 실패했습니다: ${item.name}`, error);
        }
      }
      
      return {
        success: true,
        files,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        files: [],
        error: this.getStorageErrorMessage(error.code)
      };
    }
  }

  // 파일 크기 검증
  validateFileSize(file, maxSizeMB = 10) {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return {
        valid: false,
        error: `파일 크기가 너무 큽니다. 최대 ${maxSizeMB}MB까지 업로드 가능합니다.`
      };
    }
    return { valid: true, error: null };
  }

  // 파일 타입 검증
  validateFileType(file, allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf']) {
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `지원하지 않는 파일 형식입니다. 지원 형식: ${allowedTypes.join(', ')}`
      };
    }
    return { valid: true, error: null };
  }

  // 파일명 정리 (특수문자 제거)
  sanitizeFileName(fileName) {
    return fileName
      .replace(/[^a-zA-Z0-9가-힣._-]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  }

  // 고유 파일명 생성
  generateUniqueFileName(originalName, userId = null) {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const extension = originalName.split('.').pop();
    const baseName = originalName.split('.').slice(0, -1).join('.');
    
    let fileName = `${baseName}_${timestamp}_${randomString}.${extension}`;
    
    if (userId) {
      fileName = `${userId}_${fileName}`;
    }
    
    return this.sanitizeFileName(fileName);
  }

  // URL에서 파일명 추출
  extractFileNameFromUrl(url) {
    try {
      const match = url.match(/\/o\/([^?]+)/);
      if (match) {
        const filePath = decodeURIComponent(match[1]);
        const fileName = filePath.split('/').pop();
        return fileName || null;
      }
      return null;
    } catch (error) {
      console.error('URL에서 파일명 추출 실패:', error);
      return null;
    }
  }

  // 파일 다운로드
  async downloadFile(downloadURL, fileName) {
    try {
      const response = await fetch(downloadURL);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return {
        success: true,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  // 이미지 리사이징 (클라이언트 사이드)
  async resizeImage(file, maxWidth = 800, maxHeight = 600, quality = 0.8) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        let { width, height } = img;
        
        // 비율 유지하면서 리사이징
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            });
            resolve(resizedFile);
          },
          file.type,
          quality
        );
      };
      
      img.src = URL.createObjectURL(file);
    });
  }

  // Storage 에러 메시지 변환 (한국어)
  getStorageErrorMessage(errorCode) {
    const errorMessages = {
      'storage/unauthorized': '파일에 접근할 권한이 없습니다.',
      'storage/canceled': '업로드가 취소되었습니다.',
      'storage/unknown': '알 수 없는 오류가 발생했습니다.',
      'storage/object-not-found': '파일을 찾을 수 없습니다.',
      'storage/bucket-not-found': '스토리지 버킷을 찾을 수 없습니다.',
      'storage/project-not-found': '프로젝트를 찾을 수 없습니다.',
      'storage/quota-exceeded': '스토리지 할당량을 초과했습니다.',
      'storage/unauthenticated': '인증되지 않은 요청입니다.',
      'storage/retry-limit-exceeded': '재시도 한도를 초과했습니다.',
      'storage/invalid-checksum': '파일 체크섬이 유효하지 않습니다.',
      'storage/cannot-slice-blob': '파일을 분할할 수 없습니다.',
      'storage/server-file-wrong-size': '서버 파일 크기가 일치하지 않습니다.',
      'storage/invalid-event-name': '유효하지 않은 이벤트 이름입니다.',
      'storage/invalid-url': '유효하지 않은 URL입니다.',
      'storage/invalid-argument': '유효하지 않은 인수입니다.',
      'storage/no-default-bucket': '기본 버킷이 설정되지 않았습니다.',
      'storage/cannot-serialize-blob': '파일을 직렬화할 수 없습니다.',
      'storage/retry-limit-exceeded': '재시도 한도를 초과했습니다.',
      'storage/invalid-format': '유효하지 않은 형식입니다.',
      'storage/invalid-blob': '유효하지 않은 파일입니다.',
      'storage/network-request-failed': '네트워크 요청에 실패했습니다.',
      'storage/network-unavailable': '네트워크를 사용할 수 없습니다.',
      'storage/retry-limit-exceeded': '재시도 한도를 초과했습니다.',
      'storage/invalid-format': '유효하지 않은 형식입니다.',
      'storage/invalid-blob': '유효하지 않은 파일입니다.',
      'storage/network-request-failed': '네트워크 요청에 실패했습니다.',
      'storage/network-unavailable': '네트워크를 사용할 수 없습니다.'
    };

    return errorMessages[errorCode] || `알 수 없는 스토리지 오류가 발생했습니다. (${errorCode})`;
  }
}

// 싱글톤 인스턴스 생성
const storageService = new StorageService();

export default storageService; 